import { Icon, IconButton, Typography } from '@elements';
import { Box } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import EmailIcon from '@images/email-icon.svg';
import DownloadIcon from '@images/download-icon.svg';
import ReturnTicketsIcon from '@images/return-tickets.svg';
import useDownloadOrder from '@hooks/order/useDownloadOrder';
import DownloadIconDisabled from '@images/download-icon-disabled.svg';
import useSendToEmailOrder from '@hooks/order/useSendToEmailOrder';
import EmailIconDisabled from '@images/email-icon-disabled.svg';
import { ErrorDialogContext } from '@contexts/ErrorDialogContext';
import Loader from '@components/loader/Loader';
import { getErrorMessageFromAxiosError } from '@utils/getErrorMessageFromAxiosError';
import useRefundTickets from '@hooks/ticket/useRefundTickets';
import RefundTicketModal from '@components/modals/RefundTicketModal';
import RefundSuccessModal from '@components/modals/RefundSuccessModal';

type Props = {
  orderUuid: string;
  ticketUuids: string[];
};

const ActionsDesktop = ({ orderUuid, ticketUuids }: Props) => {
  const queryClient = useQueryClient();
  const downloadOrder = useDownloadOrder(orderUuid);
  const sendToEmail = useSendToEmailOrder(orderUuid);
  const refundTickets = useRefundTickets(orderUuid);
  const [refundModalOpen, setRefundModalOpen] = React.useState(false);
  const [refundSuccessOpen, setRefundSuccessOpen] = React.useState(false);
  const isPending =
    sendToEmail.isPending || downloadOrder.isPending || refundTickets.isPending;
  const errorDialogContext = useContext(ErrorDialogContext);

  useEffect(() => {
    const error = downloadOrder.error || sendToEmail.error || refundTickets.error;

    if (error) {
      errorDialogContext.setErrorMessage(getErrorMessageFromAxiosError(error));
      errorDialogContext.setOpenErrorDialog(true);
    }
  }, [downloadOrder.error, sendToEmail.error, refundTickets.error]);

  return (
    <>
    <Box
      sx={{
        display: 'flex',
        background:
          'linear-gradient(90deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.00) 100%)',
        borderRadius: '8px',
      }}
    >
      <IconButton
        onClick={() => sendToEmail.mutate()}
        disabled={sendToEmail.isPending}
      >
        <Icon
          src={sendToEmail.isPending ? EmailIconDisabled : EmailIcon}
          width={28}
          height={28}
        />
        <Typography color="silverChalice">Отправить билеты на почту</Typography>
      </IconButton>
      <IconButton
        onClick={() => downloadOrder.mutate()}
        disabled={downloadOrder.isPending}
      >
        <Icon
          src={downloadOrder.isPending ? DownloadIconDisabled : DownloadIcon}
          width={28}
          height={28}
        />
        <Typography color="silverChalice">Скачать</Typography>
      </IconButton>
      <IconButton
        onClick={() => setRefundModalOpen(true)}
        disabled={refundTickets.isPending || ticketUuids.length === 0}
      >
        <Icon src={ReturnTicketsIcon} width={28} height={28} />
        <Typography color="silverChalice">Вернуть билеты</Typography>
      </IconButton>
    </Box>
    <RefundTicketModal
      isOpen={refundModalOpen}
      closeHandler={() => setRefundModalOpen(false)}
      acceptHandler={() => {
        refundTickets.mutate(ticketUuids, {
          onSuccess: () => {
            setRefundModalOpen(false);
            setRefundSuccessOpen(true);
          },
          onError: () => setRefundModalOpen(false),
        });
      }}
      title="Вы хотите вернуть билеты?"
    />
    <RefundSuccessModal
      isOpen={refundSuccessOpen}
      closeHandler={() => {
        setRefundSuccessOpen(false);
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      }}
    />
    <Loader isShowLoader={isPending} />
    </>
  );
};

export default ActionsDesktop;
